import { useGraphQLMutation } from '@opengov/bnp-core';
import { useQueryClient } from '@tanstack/react-query';
import { IPublicationBrandings } from '@opengov/stories-shared/types';
import { isUndefined } from 'lodash';

import { graphql } from '../../../../../gql-generated/hasura';
import {
  UpdatePublicationBrandingsForOnlinePublicationMutation,
  UpdatePublicationBrandingsForOnlinePublicationMutationVariables
} from '../../../../../gql-generated/hasura/graphql';
import { HASURA_SERVICE_URL } from '../../../../../config/config';
import { useToast } from '../../../Toast/context/useToast.context';

import { setupOnlinePublicationQueryKeys } from './setupOnlinePublicationQueryKeys';

const UpdatePublicationBrandingsForOnlinePublicationDocument = graphql(`
  mutation UpdatePublicationBrandingsForOnlinePublication(
    $id: uuid!
    $set: publicationBrandings_set_input!
  ) {
    update_publicationBrandings_by_pk(pk_columns: { id: $id }, _set: $set) {
      id
      idleBackgroundColor
      activeBackgroundColor
      activeFontColor
      createdAt
      enableLogoNavigation
      fontStyle
      idleFontColor
      isNavigationMenuSticky
      logoUrl
      publicationId
      showLogoOnFooter
      updatedAt
      enableGoogleTranslate
    }
  }
`);

export type UseSavePublicationBrandingsMessages = {
  logoUploadedTitle: string;
  logoUploadedBody?: string;
  logoRemovedTitle: string;
  logoRemovedBody?: string;
  errorTitle: string;
  errorBody?: string;
};

export type UseSavePublicationBrandingsContext = {
  publicationId: string;
};

export function useSavePublicationBrandings(
  context: UseSavePublicationBrandingsContext,
  messages: UseSavePublicationBrandingsMessages
) {
  const queryClient = useQueryClient();
  const { toggleToast } = useToast();

  return useGraphQLMutation<
    UpdatePublicationBrandingsForOnlinePublicationMutation,
    UpdatePublicationBrandingsForOnlinePublicationMutationVariables
  >({
    mutationKey: [...setupOnlinePublicationQueryKeys.all, 'savePublicationBrandings', context],
    document: UpdatePublicationBrandingsForOnlinePublicationDocument,
    url: HASURA_SERVICE_URL,
    options: {
      onSuccess: (data, variables) => {
        // Write the raw server shape so the read hook's `select()` keeps
        // returning the up-to-date row without an extra round trip. Writing
        // the normalized shape would make `select()` see `undefined` and
        // return `null`.
        const updated = data?.update_publicationBrandings_by_pk;
        if (updated) {
          queryClient.setQueryData(
            setupOnlinePublicationQueryKeys.publicationBrandings({
              publicationId: context.publicationId
            }),
            (old: { publicationBrandings: IPublicationBrandings[] } | undefined) => {
              const [first, ...rest] = old?.publicationBrandings ?? [];
              return {
                publicationBrandings: [
                  { ...(first ?? {}), ...(updated as unknown as IPublicationBrandings) },
                  ...rest
                ]
              };
            }
          );
        }

        // Original effect only toasts on logo upload/remove; other field
        // changes save silently (preview banner conveys progress).
        const logoUrl = variables.set.logoUrl;
        if (!isUndefined(logoUrl)) {
          toggleToast({
            title: logoUrl ? messages.logoUploadedTitle : messages.logoRemovedTitle,
            body: logoUrl ? messages.logoUploadedBody : messages.logoRemovedBody,
            severity: 'success'
          });
        }
      },
      onError: () =>
        toggleToast({
          title: messages.errorTitle,
          body: messages.errorBody,
          severity: 'error'
        })
    }
  });
}
